/* ============== //
||      TYPES     ||
// ============== */

import { SURVEY_LAYERS } from '../constants/SurveyLayers';
import { extractSurveyCapabilities } from '../model/surveys/SurveyImageMapping';
import { SELECT_FIELD } from './FieldReducer';
import { Survey } from '../components/Prescription/PrescriptionJob';
import { SATELLITE_LAYERS } from '../constants/SatelliteLayers';
import { SET_FARM_COUNTRY, setFarmCountry } from './FarmReducer';
import {
  resolveSurveyCountryCodeFromSurveysPayload,
  setActiveSurveyCountryCode,
} from '../model/surveys/country/SurveyCountryStrategy';
import { ReferenceValues } from './SurveyReducerTypes';
import WebAPIUtils from '../WebAPIUtils';
import { FieldLayer } from '../constants/FieldLayers';

const SET_SHOW_SOIL_SURVEYS = 'fieldsense/SurveyReducer/SET_SHOW_SOIL_SURVEYS';
const GET_ALL_SURVEYS = 'fieldsense/SurveyReducer/GET_ALL_SURVEYS';
const SET_SELECTED_LAYER = 'fieldsense/SurveyReducer/SET_SELECTED_LAYER';
const SET_VARIATIONS_ENABLED = 'fieldsense/SurveyReducer/SET_VARIATIONS_ENABLED';
const SET_CLASSIFICATIONS_ENABLED = 'fieldsense/SurveyReducer/SET_CLASSIFICATIONS_ENABLED';
const SET_STATISTICS_ENABLED = 'fieldsense/SurveyReducer/SET_STATISTICS_ENABLED';

const SAVE_SELECTED_LAYER = 'fieldsense/SurveyReducer/SAVE_SELECTED_LAYER';
const RESTORE_SELECTED_LAYER = 'fieldsense/SurveyReducer/RESTORE_SELECTED_LAYER';

const GET_REFERENCE_VALUES = 'fieldsense/SurveyReducer/GET_REFERENCE_VALUES';
const GET_CLASSIFICATION_VALUES = 'fieldsense/SurveyReducer/GET_CLASSIFICATION_VALUES';

const GET_LIMEJOB_PRESCRIPTION_VALUES = 'fieldsense/SurveyReducer/GET_LIMEJOB_PRESCRIPTION_VALUES';

const CLEAR_FIELD_SURVEY_DATA = 'fieldsense/SurveyReducer/CLEAR_FIELD_SURVEY_DATA';

/* ============== //
||    HELPERS     ||
// ============== */

function buildReferenceCacheKey(
  farmId: string,
  fieldId: string,
  layer: FieldLayer,
  basisLayer?: string,
  surveyImageId?: string,
  jobId?: number
): string {
  return `${farmId}_${fieldId}_${layer}_${surveyImageId || 'noSurvey'}_${jobId || 'noJob'}_${basisLayer || 'noBasis'}`;
}

/* ============== //
||     ACTIONS    ||
// ============== */

export function getAllSurveys(farmId: string, seasonId: string) {
  return (dispatch) => {
    return dispatch({
      type: GET_ALL_SURVEYS,
      payload: WebAPIUtils.getAllSurveys(farmId, seasonId),
    }).then((result) => {
      const payload = result && result.value ? result.value : null;
      const countryCode: string | null = resolveSurveyCountryCodeFromSurveysPayload(payload);
      if (countryCode) {
        dispatch(setFarmCountry(countryCode));
      }
      return result;
    });
  };
}

export function setShowSoilSurveys(showSoilSurveys: boolean) {
  return {
    type: SET_SHOW_SOIL_SURVEYS,
    payload: showSoilSurveys,
  };
}

export function setSelectedLayer(layer: FieldLayer) {
  return {
    type: SET_SELECTED_LAYER,
    payload: layer,
  };
}

export function setVariationsEnabled(enabled: boolean) {
  return {
    type: SET_VARIATIONS_ENABLED,
    payload: enabled,
  };
}

export function setClassificationsEnabled(enabled: boolean) {
  return {
    type: SET_CLASSIFICATIONS_ENABLED,
    payload: enabled,
  };
}

export function setStatisticsEnabled(enabled: boolean) {
  return {
    type: SET_STATISTICS_ENABLED,
    payload: enabled,
  };
}

export function saveSelectedLayer() {
  return {
    type: SAVE_SELECTED_LAYER,
  };
}

export function restoreSelectedLayer() {
  return {
    type: RESTORE_SELECTED_LAYER,
  };
}

export function getReferenceValues(
  farmId: string,
  fieldId: string,
  date: string,
  layer: FieldLayer,
  survey: { surveyImageId: string } | null,
  jobId?: number,
  measureSettings?: any,
  basisLayer?: string
) {
  return (dispatch, getState) => {
    const surveyImageId = survey?.surveyImageId;
    const cacheKey = buildReferenceCacheKey(
      farmId,
      fieldId,
      layer,
      basisLayer,
      surveyImageId,
      jobId
    );

    const cached = getState().survey.referenceCache?.[cacheKey];
    if (cached) {
      dispatch({
        type: GET_REFERENCE_VALUES + '_FULFILLED',
        meta: layer,
        payload: cached,
        fromCache: true,
      });
      return Promise.resolve(cached);
    }

    const request = WebAPIUtils.getLayerValues(
      farmId,
      fieldId,
      date,
      layer,
      survey,
      jobId,
      measureSettings,
      basisLayer
    );

    return request.then((result) => {
      dispatch({
        type: GET_REFERENCE_VALUES + '_FULFILLED',
        meta: layer,
        payload: result,
        cacheKey,
      });
      return result;
    });
  };
}

export function getClassificationValues(
  farmId: string,
  fieldId: string,
  date: string,
  layer: FieldLayer,
  survey: any,
  jobId?: number
) {
  return {
    type: GET_CLASSIFICATION_VALUES,
    meta: layer,
    payload: WebAPIUtils.getClassificationValues(farmId, fieldId, date, layer, survey, jobId),
  };
}

export function getLimeJobValues(
  farmId: number,
  fieldId: number,
  date: Date,
  survey: any,
  jobId: number
) {
  return {
    type: GET_LIMEJOB_PRESCRIPTION_VALUES,
    meta: 'LIME_VALUES',
    payload: WebAPIUtils.getMultipleLayerValues(
      farmId,
      fieldId,
      date,
      survey,
      [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY, SURVEY_LAYERS.RT],
      jobId
    ),
  };
}

export function clearFieldSurveyData(fieldId: string) {
  return {
    type: CLEAR_FIELD_SURVEY_DATA,
    payload: fieldId,
  };
}

/* ============== //
||     REDUCER    ||
// ============== */

interface State {
  variationsEnabled: boolean;
  classificationsEnabled: boolean;
  statisticsEnabled: boolean;
  surveys: Array<Survey>;
  selectedLayer: FieldLayer;
  selectedSurvey: null | Survey;
  savedSelectedSurvey: null | Survey;
  savedSelectedLayer: null | FieldLayer;
  showSoilSurveys: boolean;
  surveyViewCapabilities: any;
  restoreSavedSelectedSurvey: boolean;
  selectedSurveyReferenceValues: ReferenceValues;
  selectedSurveyClassificationValues: any;
  referenceCache: any;
  restoreSaved: boolean;
}

const initialState: State = {
  variationsEnabled: false,
  classificationsEnabled: false,
  statisticsEnabled: true,
  surveys: [],
  selectedLayer: SATELLITE_LAYERS.VITALITY_NDVI,
  selectedSurvey: null,
  savedSelectedSurvey: null,
  savedSelectedLayer: null,
  showSoilSurveys: false,
  surveyViewCapabilities: undefined,
  restoreSavedSelectedSurvey: false,
  selectedSurveyReferenceValues: {},
  selectedSurveyClassificationValues: {},
  referenceCache: {},
  restoreSaved: false,
};

const SET_SELECTED_SURVEY_FORCEFULLY = 'fieldsense/SurveyReducer/SET_SELECTED_SURVEY_FORCEFULLY';
const SET_SELECTED_SURVEY_CLASSIFICATIONS =
  'fieldsense/SurveyReducer/SET_SELECTED_SURVEY_CLASSIFICATIONS';

export function setSelectedSurveyForcefully(survey: Survey) {
  return {
    type: SET_SELECTED_SURVEY_FORCEFULLY,
    payload: survey,
  };
}

export function setSelectedSurveyClassifications(classifications, layer) {
  return {
    type: SET_SELECTED_SURVEY_CLASSIFICATIONS,
    payload: classifications,
    meta: layer,
  };
}

function reducer(state: State = initialState, action) {
  switch (action.type) {
    case SET_SHOW_SOIL_SURVEYS: {
      state = { ...state, showSoilSurveys: action.payload };
      break;
    }

    case SET_CLASSIFICATIONS_ENABLED: {
      state = { ...state, classificationsEnabled: action.payload };
      break;
    }

    case SET_VARIATIONS_ENABLED: {
      state = { ...state, variationsEnabled: action.payload };
      break;
    }

    case SET_STATISTICS_ENABLED: {
      state = { ...state, statisticsEnabled: action.payload };
      break;
    }

    case GET_ALL_SURVEYS + '_FULFILLED': {
      const payload = action.payload;
      if (payload) {
        const { surveys } = payload;
        const sortedSurveys = surveys.sort(
          (a: any, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
        );

        const surveyViewCapabilities = extractSurveyCapabilities(sortedSurveys);
        state = { ...state, surveys: sortedSurveys, surveyViewCapabilities };
      }
      break;
    }

    case GET_ALL_SURVEYS + '_REJECTED': {
      state = { ...state, surveys: [] };
      break;
    }

    case SET_SELECTED_LAYER: {
      state = { ...state, selectedLayer: action.payload };
      break;
    }

    case SAVE_SELECTED_LAYER: {
      state = { ...state, savedSelectedLayer: state.selectedLayer };
      break;
    }

    case RESTORE_SELECTED_LAYER: {
      if (state.savedSelectedLayer) {
        state = { ...state, selectedLayer: state.savedSelectedLayer, savedSelectedLayer: null };
      }
      break;
    }
    case SELECT_FIELD: {
      let newSurvey = null;
      const fieldId = action.payload && action.payload.fieldId;
      if (fieldId) {
        newSurvey = state.surveys.find((s: Survey) => s.fieldId === fieldId);
      }
      state = {
        ...state,
        selectedSurvey: newSurvey,
        selectedSurveyReferenceValues: {},
        selectedSurveyClassificationValues: {},
      };
      break;
    }

    case SET_SELECTED_SURVEY_CLASSIFICATIONS: {
      state = {
        ...state,
        selectedSurveyClassificationValues: {
          ...state.selectedSurveyClassificationValues,
          [action.meta]: action.payload,
        },
      };

      break;
    }

    case SET_SELECTED_SURVEY_FORCEFULLY: {
      const survey = action.payload;
      if (survey) {
        const surveyViewCapabilities = extractSurveyCapabilities([...state.surveys, survey]);
        state = {
          ...state,
          savedSelectedSurvey: state.selectedSurvey,
          selectedSurvey: survey,
          surveyViewCapabilities,
          restoreSaved: state.restoreSavedSelectedSurvey != null,
        };
      } else {
        const surveyViewCapabilities = extractSurveyCapabilities(state.surveys);
        if (state.restoreSavedSelectedSurvey) {
          state = {
            ...state,
            savedSelectedSurvey: null,
            selectedSurvey: state.savedSelectedSurvey,
            surveyViewCapabilities,
            restoreSavedSelectedSurvey: false,
          };
        }
      }
      break;
    }
    case GET_REFERENCE_VALUES + '_FULFILLED': {
      const newCache = { ...state.referenceCache };
      if (action.cacheKey && !action.fromCache) {
        newCache[action.cacheKey] = action.payload;
      }

      state = {
        ...state,
        selectedSurveyReferenceValues: {
          ...state.selectedSurveyReferenceValues,
          [action.meta]: action.payload,
        },
        referenceCache: newCache,
      };
      break;
    }
    case GET_CLASSIFICATION_VALUES + '_FULFILLED': {
      state = {
        ...state,
        selectedSurveyClassificationValues: {
          ...state.selectedSurveyClassificationValues,
          [action.meta]: action.payload,
        },
      };
      break;
    }

    case GET_LIMEJOB_PRESCRIPTION_VALUES + '_FULFILLED': {
      const [humusValues, clayValues, reactionNumbers] = action.payload;

      state = {
        ...state,
        selectedSurveyReferenceValues: {
          ...state.selectedSurveyReferenceValues,
          [SURVEY_LAYERS.HUMUS]: humusValues,
          [SURVEY_LAYERS.CLAY]: clayValues,
          [SURVEY_LAYERS.RT]: reactionNumbers,
        },
      };
      break;
    }

    case SET_FARM_COUNTRY: {
      setActiveSurveyCountryCode(action.payload);
      const surveyViewCapabilities = extractSurveyCapabilities(state.surveys);
      state = { ...state, surveyViewCapabilities };
      break;
    }

    case CLEAR_FIELD_SURVEY_DATA: {
      const updatedSurveys = state.surveys.filter((survey) => survey.fieldId !== action.payload);

      // Clear cache entries related to the fieldId
      const updatedCache = Object.keys(state.referenceCache).reduce((cache, key) => {
        if (!key.includes(`_${action.payload}_`)) {
          cache[key] = state.referenceCache[key];
        }
        return cache;
      }, {});

      state = {
        ...state,
        surveys: updatedSurveys,
        selectedSurvey:
          state.selectedSurvey?.fieldId === action.payload ? null : state.selectedSurvey,
        selectedSurveyReferenceValues: {},
        selectedSurveyClassificationValues: {},
        referenceCache: updatedCache,
      };
      break;
    }

    default:
      break;
  }

  return state;
}

export default reducer;
